import * as R from 'ramda';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import Typist, { TypistProps } from 'react-typist';
import 'react-typist/dist/Typist.css';
import useLocalStorage from '@rehooks/local-storage';

type TypistItemProps = {
  className?: string,
  children: React.ReactNode,
} & TypistProps;

enum TypistSpeed {
  NORMAL = 'NORMAL',
  FAST = 'FAST',
  DISABLED = 'DISABLED',
}

export enum TypistDelayLength {
  short = 500,
  normal = 1000,
  long = 1500,
  longest = 2500,
}

type TypistContextValue = {
  speed: TypistSpeed;
  setSpeed: (speed: TypistSpeed) => void;
};

const TypistContext = React.createContext<TypistContextValue>({
  speed: TypistSpeed.DISABLED,
  setSpeed: () => {},
});

export function TypistSpeedProvider({ children }: { children: JSX.Element | JSX.Element[] }) {
  const [speed, setSpeed] = useLocalStorage<TypistSpeed>('typingSpeed');
  const value = useMemo(() => ({
    speed: speed || TypistSpeed.DISABLED,
    setSpeed,
  }), [speed, setSpeed]);
  return (
    <TypistContext.Provider value={value}>
      {children}
    </TypistContext.Provider>
  );
}

export function TypistSpeedControl({ className }: { className: string }) {
  const { speed, setSpeed } = useContext(TypistContext);
  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSpeed(event.target.value as TypistSpeed);
  };
  return (
    <div className={className}>
      <FormControl>
        <InputLabel id="typing-speed-selector-label">Typing animation</InputLabel>
        <Select
          id="typing-speed-selector"
          value={speed}
          onChange={handleChange}
        >
          <MenuItem value={TypistSpeed.NORMAL}>Normal</MenuItem>
          <MenuItem value={TypistSpeed.FAST}>Fast</MenuItem>
          <MenuItem value={TypistSpeed.DISABLED}>Disabled</MenuItem>
        </Select>
      </FormControl>
    </div>
  );
}

const PARAMETERS_BY_SPEED: Record<TypistSpeed, TypistProps> = {
  [TypistSpeed.NORMAL]: {
    avgTypingDelay: 30,
    stdTypingDelay: 15,
  },
  [TypistSpeed.FAST]: {
    avgTypingDelay: 10,
    stdTypingDelay: 5,
  },
  [TypistSpeed.DISABLED]: {
    avgTypingDelay: 0,
    stdTypingDelay: 0,
    startDelay: 0,
  },
};

const DELAY_MULTIPLIER_BY_SPEED: Partial<Record<TypistSpeed, number>> = {
  [TypistSpeed.NORMAL]: 1,
  [TypistSpeed.FAST]: 0.5,
};

export function TypistItem({ className, children }: TypistItemProps) {
  return (
    <p className={className}>
      {children}
    </p>
  );
}
export function TypistAfterFinish({ children }: { children: React.ReactElement }) {
  return children;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function TypistDelay({ length }: { length: TypistDelayLength }) {
  return null;
}

export function TypistWrapper(
  {
    children,
    startDelay,
    interDelay,
    endDelay = false,
    onFinish,
  }: {
    children: JSX.Element | JSX.Element[];
    startDelay?: TypistDelayLength,
    interDelay?: TypistDelayLength;
    endDelay?: boolean;
    onFinish?: () => void;
  },
) {
  const [finished, setFinished] = useState(false);
  const { speed } = useContext(TypistContext);
  const [current, setCurrent] = useState(0);
  const speedChangeReset = useRef(false);
  const typistSequence = useMemo(
    () => R.fromPairs(R.aperture(
      2,
      React.Children.map(
        children,
        (child, idx) => ((child as any).type === TypistItem ? idx : undefined),
      ),
    )),
    [children],
  );
  const increaseCurrent = useCallback(
    () => setCurrent((val) => typistSequence[val]),
    [typistSequence],
  );
  const cursorProps: Typist.CursorProps = { hideWhenDone: true, hideWhenDoneDelay: 0 };
  const animDisabled = speed === TypistSpeed.DISABLED;
  useEffect(
    () => {
      if (!speedChangeReset.current) {
        speedChangeReset.current = true;
      } else {
        setCurrent(-1);
        setTimeout(() => setCurrent(0), 100);
      }
    },
    [speed],
  );
  useEffect(
    () => {
      if (!finished && (animDisabled || current === undefined)) {
        setFinished(true);
        onFinish?.();
      }
    },
    [current, finished, animDisabled, onFinish],
  );
  const delayMultiplier = DELAY_MULTIPLIER_BY_SPEED[speed] || 1;
  const betweenItemsDelay = (interDelay ?? TypistDelayLength.normal) * delayMultiplier;
  const firstItem = !Object.values(typistSequence).some((item) => current === item);
  return (
    <>
      {React.Children.map(children, (child, idx) => {
        if (child.type === TypistItem) {
          if (animDisabled) {
            return (
              <p className={child.props.className}>
                {child.props.children}
              </p>
            );
          }
          if (current !== undefined && current < idx) return null;
          return (
            <div className={child.props.className}>
              <Typist
                onTypingDone={increaseCurrent}
                cursor={cursorProps}
                avgTypingDelay={PARAMETERS_BY_SPEED[speed].avgTypingDelay}
                stdTypingDelay={PARAMETERS_BY_SPEED[speed].stdTypingDelay}
              >
                {Boolean(startDelay && firstItem) && <Typist.Delay ms={startDelay!} />}
                {React.Children.map(child.props.children, (itemChild) => {
                  if (itemChild.type === TypistDelay) {
                    if (animDisabled) return null;
                    return (
                      <Typist.Delay
                        ms={itemChild.props.length as TypistDelayLength * delayMultiplier}
                      />
                    );
                  }
                  return itemChild;
                })}
                {Boolean(endDelay || (interDelay && typistSequence[current]))
                  && <Typist.Delay ms={betweenItemsDelay} />}
              </Typist>
            </div>
          );
        }
        if (child.type === TypistAfterFinish) {
          if (current === undefined || animDisabled) {
            return child.props.children;
          }
          return null;
        }
        return child;
      })}
    </>
  );
  // return (
  //   <>
  //     {childrenTypists.map((child, idx) => (idx <= current
  //       ? React.cloneElement(child, {
  //         // eslint-disable-next-line react/no-array-index-key
  //         key: `key-${idx}`,
  //         onTypingDone: () => {
  //           child.props.onTypingDone?.(); setCurrent(idx + 1);
  //           if (idx === childrenTypists.length - 1 && onFinish) onFinish();
  //         },
  //         startDelay: idx === 0 && startDelay ? startDelay : undefined,
  //         afterDelay: child.props.afterDelay ?? interDelay,
  //         cursor: idx !== childrenTypists.length ? { hideWhenDoneDelay: 0 } : undefined,
  //         avgTypingDelay: slowness,
  //       })
  //       : null))}
  //   </>
  // );
}
